/**
 * oRPC Client with Isomorphic Pattern
 *
 * - Server (SSR): Lazy-loaded router with dynamic imports (prevents pg in client bundle)
 * - Client: HTTP fetch to /api endpoint
 *
 * Uses type-only imports and dynamic imports to avoid bundling server dependencies.
 */

// Type-only import (erased at build time) - doesn't bundle pg
import type { appRouter as AppRouterType } from "@designs-by-maryam/api/routers/index";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { SimpleCsrfProtectionLinkPlugin } from "@orpc/client/plugins";
import { createRouterClient, type RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { isServer, QueryCache, QueryClient } from "@tanstack/react-query";
import { createIsomorphicFn } from "@tanstack/react-start";
import { toast } from "sonner";

// Type alias for the router (type-only, doesn't bundle server code)
type AppRouter = typeof AppRouterType;

/**
 * Per-request QueryClient factory.
 * - Server: New instance per request (prevents cache bleeding)
 * - Client: Singleton for browser session
 */
export const createQueryClient = () =>
	new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 30 * 60 * 1000, // 30 minutes
				refetchOnWindowFocus: false,
				retry: 2,
				retryDelay: (attemptIndex) =>
					Math.min(1000 * 2 ** attemptIndex, 30_000),
			},
		},
		queryCache: new QueryCache({
			onError: (error) => {
				// Only toast on client (SSR-safe)
				if (!isServer) {
					toast.error(`Error: ${error.message}`);
				}
			},
		}),
	});

// Per-request QueryClient for SSR isolation
let browserQueryClient: QueryClient | undefined;

export function getQueryClient(): QueryClient {
	if (isServer) {
		// Always create a new QueryClient on the server
		// This prevents cache bleeding between requests
		return createQueryClient();
	}
	// Browser: reuse singleton
	if (!browserQueryClient) {
		browserQueryClient = createQueryClient();
	}
	return browserQueryClient;
}

// Legacy export for backwards compatibility
export const queryClient = getQueryClient();

/**
 * Lazy server client with dynamic imports.
 * Avoids static linking of appRouter/createContext which would bundle pg.
 */
function createLazyServerClient(): RouterClient<AppRouter> {
	let cached: RouterClient<AppRouter> | undefined;

	// Dynamic imports - only executed on server, not bundled for client
	const clientPromise = Promise.all([
		import("@designs-by-maryam/api/routers/index"),
		import("@designs-by-maryam/api/context"),
	]).then(([routerMod, contextMod]) =>
		createRouterClient(routerMod.appRouter, {
			context: async ({ req }) => contextMod.createContext({ req }),
		}),
	);

	const getClient = async () => {
		if (!cached) {
			cached = await clientPromise;
		}
		return cached;
	};

	// Proxy that forwards calls to the lazy-loaded client
	return new Proxy(
		{},
		{
			get(_target, prop) {
				// Avoid being treated as a Promise
				if (prop === "then") {
					return undefined;
				}
				// Return async handler that waits for client
				return async (...args: unknown[]) => {
					const client = await getClient();
					const value = (client as Record<PropertyKey, unknown>)[prop];
					if (typeof value === "function") {
						return value.apply(client, args);
					}
					return value;
				};
			},
		},
	) as RouterClient<AppRouter>;
}

/**
 * Isomorphic oRPC client
 *
 * Server: Lazy proxy with dynamic imports (prevents pg bundling)
 * Client: RPCLink with CSRF protection
 */
const getORPCClient = createIsomorphicFn()
	.server(() => createLazyServerClient())
	.client(() => {
		const link = new RPCLink({
			url: `${window.location.origin}/api`,
			plugins: [new SimpleCsrfProtectionLinkPlugin()],
			fetch(_url, options) {
				return fetch(_url, {
					...options,
					credentials: "include",
				});
			},
		});
		// createORPCClient returns a client-side compatible type
		// Cast to RouterClient<AppRouter> to unify with server-side type
		return createORPCClient(link) as unknown as RouterClient<AppRouter>;
	});

// Properly typed client export
// Cast to ensure RouterClient type flows through createIsomorphicFn
export const client = getORPCClient() as RouterClient<AppRouter>;
// createTanstackQueryUtils infers types from client, no generic needed
export const orpc = createTanstackQueryUtils(client);
